import {
  AgentThreadControlSignalDtoSignal,
  AgentThreadVoStatus,
  api,
  ContentType,
} from "@/api";
import type { AgentThreadTaskStatus, AgentThreadVo } from "@/api/Api";
import { RawArtifact } from "@/types/artifact";
import { AspectRatio, Resolution } from "@/types/common";
import { AvatarAudioType } from "@/types/mediaArtifact";
import { action, makeAutoObservable, observable } from "mobx";

type SetKeys = keyof Pick<
  ThreadStore,
  | "loading"
  | "threads"
  | "thread"
  | "threadId"
  | "rawArtifacts"
  | "artifactsRequestStatus"
  | "rtcTasks"
  | "threadCredits"
  | "threadOptions"
>;

export interface ThreadOptionsResponse {
  aspect_ratio?: AspectRatio;
  resolution?: Resolution;
  enable_shots?: boolean;
}

export type ArtifactsRequestStatus =
  | "idle"
  | "loading"
  | "success"
  | "not_found"
  | "error";

export interface GenerateImageForShotByIndexTask {
  tool_type: "generate_image_for_shot_by_index";
  init_args: {
    i2i_model_name: string;
    t2i_model_name: string;
    user_prompt: string;
    user_specified_images?: string[];
  };
  call_args: {
    shot_index: number;
  };
}

export interface GenerateVideoForShotByIndexTask {
  tool_type: "generate_video_for_shot_by_index";
  init_args: {
    t2v_model_name: string;
    i2v_model_name: string;
    is2v_model_name: string;
    user_prompt: string;
    line_content: string;
    user_specified_images: string[];
    avatar_voice_id: string;
    avatar_voice_path: string;
    generate_type: "normal" | "avatar";
    avatar_audio_type?: AvatarAudioType;
  };
  call_args: {
    shot_index: number;
  };
}

export interface GenerateReferenceImageByIndexTask {
  tool_type: "generate_reference_image_by_index";
  init_args: {
    i2i_model_name: string;
    t2i_model_name: string;
    user_prompt: string;
    user_specified_images: string[];
  };
  call_args: {
    index: number;
  };
}

export interface RenderVideoTask {
  tool_type: "render_video";
  init_args: Record<string, unknown>;
  call_args: Record<string, unknown>;
}

export interface RtcTask {
  id: string;
  task_type: string;
  thread_id: string;
  data:
    | GenerateImageForShotByIndexTask
    | GenerateVideoForShotByIndexTask
    | GenerateReferenceImageByIndexTask
    | RenderVideoTask;
  status: AgentThreadTaskStatus;
  message?: string;
  create_time: string;
  update_time: string;
}

export interface ThreadCredits {
  total: number;
  text: number;
  image: number;
  video: number;
  audio: number;
}

export class ThreadStore {
  @observable threads: AgentThreadVo[] = [];
  @observable loading: boolean = false;
  @observable threadsLoading: boolean = false;
  @observable updateUserLoading: boolean = false;
  @observable thread: AgentThreadVo | null = null;
  @observable threadId: string | null = null;
  @observable rawArtifacts: RawArtifact | null = null;
  @observable artifactsRequestStatus: ArtifactsRequestStatus = "idle";
  @observable rtcTasks: RtcTask[] = [];
  @observable threadCredits: ThreadCredits | null = null;
  @observable threadOptions: {
    aspectRatio: AspectRatio;
    resolution: Resolution;
    enableShots: boolean;
  } = {
    aspectRatio: AspectRatio.LANDSCAPE,
    resolution: Resolution.HD,
    enableShots: false,
  };

  isPosterUpdated: boolean = false;

  constructor() {
    // 使用装饰器时不需要 makeAutoObservable
    makeAutoObservable(this);
  }

  get isThreadHangUp() {
    return (
      this.thread?.status === AgentThreadVoStatus.Failed ||
      this.thread?.status === AgentThreadVoStatus.Terminated
    );
  }

  @action setThreadId(id: string) {
    if (this.threadId && this.threadId === id) {
      return;
    }
    this.threadId = id;
    this.set("thread", null);
    this.set("rawArtifacts", null);
    this.set("artifactsRequestStatus", "idle");
    this.set("rtcTasks", []);
    this.set("threadCredits", null);
    this.set("threadOptions", {
      aspectRatio: AspectRatio.LANDSCAPE,
      resolution: Resolution.HD,
      enableShots: false,
    });
  }

  @action clearThread() {
    this.threadId = null;
    this.set("thread", null);
    this.set("rawArtifacts", null);
    this.set("artifactsRequestStatus", "idle");
    this.set("rtcTasks", []);
    this.set("threadCredits", null);
    this.set("threadOptions", {
      aspectRatio: AspectRatio.LANDSCAPE,
      resolution: Resolution.HD,
      enableShots: false,
    });
    this.isPosterUpdated = false;
  }

  @action set<K extends SetKeys>(k: K, v: ThreadStore[K]) {
    if (k === "thread") {
      const incomingThread = v as AgentThreadVo | null;
      if (!incomingThread) {
        this.thread = null;
        return;
      }

      const nextThread: AgentThreadVo = { ...incomingThread };

      // 如果 incomingThread 没有 status，则使用 previous status
      if (!nextThread.status && this.thread?.status) {
        nextThread.status = this.thread.status;
      }

      this.thread = nextThread;
      return;
    }

    this[k] = v as never;
  }

  @action setThreadOptions(options: ThreadOptionsResponse) {
    this.threadOptions = {
      aspectRatio: options.aspect_ratio || AspectRatio.LANDSCAPE,
      resolution: options.resolution || Resolution.HD,
      enableShots: !!options.enable_shots,
    };
  }

  @action async fetchThread(id: string): Promise<AgentThreadVo> {
    const res = await api.threads.getThreadDetail(id);
    const thread = res.data;
    this.set("thread", thread);
    return thread;
  }

  @action async stopRunning() {
    if (!this.threadId) {
      return;
    }
    await api.threads.controlThread(this.threadId, {
      signal: AgentThreadControlSignalDtoSignal.ValueCtrlStopImmediately,
    });
    this.set("thread", {
      ...this.thread,
      status: AgentThreadVoStatus.Stopping,
    });
  }

  @action async updateThreadPoster(posterUrl: string) {
    if (
      !this.thread ||
      !this.thread.owner ||
      this.thread.imageUrl ||
      this.isPosterUpdated
    ) {
      return;
    }
    const res = await api.threads.updateThread(this.thread.id!, {
      imageUrl: posterUrl,
    });
    this.set("thread", res.data);
    this.isPosterUpdated = true;
    return res.data;
  }

  @action async fetchThreads(options?: {
    offset?: number;
    limit?: number;
  }): Promise<AgentThreadVo[]> {
    this.threadsLoading = true;
    try {
      const res = await api.threads.listThreads({
        offset: options?.offset,
        limit: options?.limit,
      });
      this.set("threads", res.data?.list || []);
      return res.data?.list || [];
    } finally {
      this.threadsLoading = false;
    }
  }

  @action async sendMessage(
    threadId: string,
    data: { files: File[]; text: string }
  ) {
    const formData = new FormData();

    for (const file of data.files) {
      formData.append("files", file);
    }

    formData.append("text", data.text);

    await api.request<AgentThreadVo, unknown>({
      path: `/api/v1/threads/${threadId}/messages`,
      method: "POST",
      body: formData,
      type: ContentType.FormData,
    });
  }

  @action async createThread(data: {
    urls: string[];
    user_input: string;
    auto_mode: boolean;
    aspect_ratio: AspectRatio;
    resolution: Resolution;
    knowledge_base_id?: string;
    default_models?: Record<string, string>;
  }) {
    const formData = new FormData();

    formData.append("file_urls", JSON.stringify(data.urls));
    formData.append("user_input", data.user_input);
    formData.append("type", "interactive");
    formData.append("auto_mode", data.auto_mode.toString());
    formData.append(
      "options",
      JSON.stringify({
        aspect_ratio: data.aspect_ratio,
        resolution: data.resolution,
        knowledge_base_id: data.knowledge_base_id,
        ...data.default_models, // Spread default models directly into options
      })
    );

    try {
      const res = await api.request<AgentThreadVo, unknown>({
        path: `/api/v1/threads`,
        method: "POST",
        body: formData,
        type: ContentType.FormData,
      });

      this.fetchThreads();
      return res.data;
    } finally {
      this.updateUserLoading = false;
    }
  }

  @action async updateThreadTitle(threadId: string, title: string) {
    const res = await api.threads.updateThread(threadId, {
      title,
    });
    this.set("thread", res.data);
    return res.data;
  }

  @action async fetchThreadCredits(threadId: string) {
    if (!this.thread?.owner) {
      return;
    }

    try {
      const res = await api.threads.getThreadCredits(threadId);

      if (res.data) {
        this.set("threadCredits", {
          total: res.data.totalCredits || 0,
          text: res.data.creditsByReasonSubType?.["TEXT_GENERATION"] || 0,
          image: res.data.creditsByReasonSubType?.["IMAGE_GENERATION"] || 0,
          video: res.data.creditsByReasonSubType?.["VIDEO_GENERATION"] || 0,
          audio: res.data.creditsByReasonSubType?.["AUDIO_GENERATION"] || 0,
        } as ThreadCredits);
      }

      return res.data;
    } catch (error) {
      console.error("Failed to fetch thread credits:", error);
    }
  }
}

export const threadStore = new ThreadStore();
